import { useMemo, useRef, useState } from 'react';

import { downloadBlob } from '@mint-lib/api-zod-support';
import { useTranslation } from '@mint-lib/i18n';
import { Download, Import, Upload } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useFlags } from '@mint-lib/routing-context';
import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@mint-lib/tw-components';
import {
  type BaseFile,
  type FileUploaderV2RefType,
  type ReadyForUploadFile,
} from '@mint-lib/tw-components/patterns';

import { useImportActivity } from '../../../../../../api/mutations/useImportActivity.js';
import { useImportTemplate } from '../../../../../../api/queries/useImportTemplate.js';
import { useSidebarTreeApi } from '../../../../../../api/queries/useSidebarTreeApi.js';
import { useActivityByMediaDataId } from '../../../../../../hooks/useActivityByMediaDataId.js';
import { useChosenLevel } from '../../../../../../hooks/useChosenLevel.js';
import { ImportPlanViews } from '../../../../../../types/import.types.js';
import ModalViewFactory from './components/ModalViewFactory.jsx';

type Props = {
  handleOpenChange: (isOpen: boolean) => void;
};

export const DialogContentImport = ({ handleOpenChange }: Props) => {
  const { t } = useTranslation('@myn/planning_v2');

  const ff = useFlags('@mint/arm', ['disableActivityLevel']);
  const isActivityLevelDisabled = ff.disableActivityLevel.enabled;

  const { data: sidebar } = useSidebarTreeApi();
  const activities = sidebar?.subItems ?? [];

  const [selectedActivityId, setSelectedActivityId] = useState<
    string | undefined
  >();

  const { id: chosenId } = useChosenLevel();
  const chosenActivity = useActivityByMediaDataId(chosenId);

  const activity = useMemo(() => {
    if (!isActivityLevelDisabled) {
      return chosenActivity ?? undefined;
    }

    return activities.find((item) => item.activityId === selectedActivityId);
  }, [isActivityLevelDisabled, chosenActivity, activities, selectedActivityId]);

  const {
    data: errorLog,
    mutateAsync: importAsync,
    reset: resetImport,
    isIdle,
    isPending,
  } = useImportActivity();

  const {
    mutateAsync: downloadImportTemplateAsync,
    isPending: isDownloadingTemplate,
  } = useImportTemplate();

  const [files, setFiles] = useState<BaseFile[]>([]);

  const fileUploaderRef = useRef<FileUploaderV2RefType | null>(null);

  const isFileReadyForUpload = files.some((file) =>
    file.isReadyForUploadFile(),
  );

  const handleFileUpload = async (fileBlob: File) => {
    await importAsync(
      {
        file: fileBlob,
        activityId: activity?.activityId || '',
      },
      {
        onSuccess: (errorLog) => {
          if (errorLog) return;

          enqueueSnackbar({
            title: t('Media plan imported successfully'),
            variant: 'success',
            removable: true,
          });
          handleOpenChange(false);
        },
        onError: (err) => {
          enqueueSnackbar({
            title: err.message,
            variant: 'error',
            removable: true,
          });

          handleOpenChange(false);
        },
      },
    );
  };

  const handleDownloadTemplateButton = async () => {
    if (!activity?.activityId) {
      return;
    }
    downloadImportTemplateAsync(activity.activityId);
  };

  const handleDownloadErrorLog = async () => {
    if (!errorLog) {
      return;
    }

    downloadBlob(errorLog.file, errorLog.fileName);
  };

  const handleUploadNewFileButton = () => {
    resetImport();
    setFiles([]);
  };

  const handleImport = () => {
    if (!fileUploaderRef.current) return;

    const readyForUploadFiles = files.filter(
      (file): file is ReadyForUploadFile => file.isReadyForUploadFile(),
    );

    fileUploaderRef.current.uploadFiles(readyForUploadFiles);
  };

  const isImportButtonDisabled =
    (isIdle && !isFileReadyForUpload) ||
    !!errorLog ||
    isPending ||
    (isActivityLevelDisabled && !selectedActivityId);

  const activeView = useMemo(() => {
    if (errorLog) {
      return ImportPlanViews.ERROR;
    }

    if (isPending) {
      return ImportPlanViews.PENDING;
    }

    return ImportPlanViews.UPLOAD;
  }, [errorLog, isPending]);

  return (
    <DialogRoot size="m">
      <DialogHeader>
        <DialogTitle>{t('Import activity')}</DialogTitle>
      </DialogHeader>

      <DialogContent>
        <div className="flex w-full flex-col p-1">
          <ModalViewFactory
            activeView={activeView}
            props={{
              fileUploaderRef,
              setFiles,
              handleFileUpload,
              activities,
              selectedActivityId,
              onActivityChange: setSelectedActivityId,
            }}
          />
        </div>
      </DialogContent>

      <DialogFooter>
        <div className="flex w-full justify-between">
          {isIdle && (
            <Button
              disabled={
                isDownloadingTemplate ||
                (isActivityLevelDisabled && !selectedActivityId)
              }
              variant="text"
              onClick={handleDownloadTemplateButton}
            >
              <Download />
              {t('Download template (.xlsx)')}
            </Button>
          )}
          {errorLog && (
            <div className="flex gap-2">
              <Button variant="outlined" onClick={handleUploadNewFileButton}>
                <Upload />
                {t('Upload new file')}
              </Button>
              <Button variant="text" onClick={handleDownloadErrorLog}>
                <Download />
                {t('Download error log')}
              </Button>
            </div>
          )}
          <div className="ml-auto flex gap-2">
            <Button
              disabled={isPending}
              onClick={() => handleOpenChange(false)}
              variant="text"
            >
              {t('Cancel')}
            </Button>

            <Button onClick={handleImport} disabled={isImportButtonDisabled}>
              <Import />
              {t('Import')}
            </Button>
          </div>
        </div>
      </DialogFooter>
    </DialogRoot>
  );
};
